import CompanyDivisionModal from '../../components/Modal/CompanyDivisionModal';
import { MigrationCandidateBusinessPartnerBody } from '../../api/generated/pm';
import { getV1IdentityCurrentMigrationCandidateBusinessPartnersQueryKey } from '../../api/generated/pm/@tanstack/react-query.gen';
import { queryClient } from '../../api/queryClient';

interface Props {
  organization: MigrationCandidateBusinessPartnerBody | null;
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
}

/**
 * Wraps the standalone `CompanyDivisionModal` for the "Add as new
 * organization" entry point from a reviewed Migration Candidate.
 *
 * Maps the candidate's `name`/`address` into `CompanyDivisionModal`'s
 * `defaultValues` so the General Information step arrives pre-filled, always
 * passes `showVerifyBanner`/`alwaysEditableAddress` so the user is asked to
 * verify the pre-filled data with directly-editable address fields. On a
 * successful submission it invalidates the Migration Candidate list query
 * (the same key `PendingMerges` / `useMigrationCandidateBusinessPartners`
 * reads) so the candidate's row disappears — no explicit accept/reject call,
 * since the backend transitions the candidate's status on its own.
 *
 * Passes `organization.dealerBusinessPartnerId` straight through as
 * `migrationCandidateDealerId`: the candidate's dealer (see `GLOSSARY.md`) is
 * authoritative, so this modal does no lookup of its own — resolving a
 * display name/address for the dealer is entirely `CompanyDivisionModal`'s
 * concern now.
 *
 * Also passes `organization.id` through as `migrationCandidateBusinessPartnerId`
 * so the built request body links back to the candidate and the backend can
 * transition its status.
 */
function AddOrganizationFromMigrationCandidateModal({ organization, open, onClose }: Props) {
  const handleClose = (shouldUpdate: boolean) => {
    if (shouldUpdate) {
      queryClient.invalidateQueries({
        queryKey: getV1IdentityCurrentMigrationCandidateBusinessPartnersQueryKey(),
      });
    }
    onClose(shouldUpdate);
  };

  const defaultValues = organization
    ? {
        name: organization.name,
        ...organization.address,
      }
    : undefined;

  // Mount `CompanyDivisionModal` fresh each time the modal opens (mirroring the
  // standalone `{createModalOpen && <CompanyDivisionModal .../>}` pattern).
  // `useBusinessPartnerForm` seeds its state via `useState` only on mount, so a
  // persistent mount would ignore `defaultValues`/`migrationCandidateDealerId` that
  // only become available once a candidate is chosen — leaving the form blank and
  // the dealer unselected.
  if (!open) return null;

  return (
    <CompanyDivisionModal
      open={open}
      onClose={handleClose}
      defaultValues={defaultValues}
      showVerifyBanner
      alwaysEditableAddress
      migrationCandidateDealerId={organization?.dealerBusinessPartnerId}
      migrationCandidateBusinessPartnerId={organization?.id}
    />
  );
}

export default AddOrganizationFromMigrationCandidateModal;
